/** Domain types shared by the API layer, the local store, and the UI. */

import type { Measurement } from '~/data/weighins'
import type { PlanEntry } from '~/data/templates'

/**
 * A logging method: what a set records. A squat records weight and reps, a plank duration, a
 * weighted pull up reps and weight. One exercise can be logged more than one way, so this is
 * the set's own — read back from the numbers it holds, not from anything it points at.
 */
export interface Measurements {
  readonly weight: boolean
  readonly reps: boolean
  readonly duration: boolean
  readonly distance: boolean
}

/** What an exercise records before it has a name: nothing, because it has no method yet. */
export const NOTHING_RECORDED: Measurements = {
  weight: false,
  reps: false,
  duration: false,
  distance: false,
}

export type Mood = 'great' | 'okay' | 'bad' | 'sick' | 'tired'

export type Units = 'kg' | 'lb'

/** What a distance is read in. Chosen apart from weight — plenty lift in one and run in the other. */
export type DistanceUnits = 'km' | 'mi'

/**
 * An entry in the built-in catalog: a name to offer, the logging method it usually takes, and
 * the step its weight rounds to. Read only — nothing adds to the catalog or changes it.
 */
export interface Exercise {
  readonly id: string
  readonly name: string
  readonly records: Measurements
  /** Step the weight input rounds to, in kilograms. Null means no snapping. */
  readonly weightSnapping: number | null
}

/**
 * An exercise as the exercises row wants it: what it was last called, how it was last logged,
 * when that was, and its thumbnail. It is identified by its name — there is nothing else.
 */
export interface RecentExercise {
  readonly name: string
  readonly records: Measurements
  readonly lastPerformedAt: number
  /** The latest set with a thumbnail, or null when nothing of it was ever filmed. */
  readonly thumbnailSetId: string | null
}

/**
 * A track that was playing while a set was performed. Read off the phone during a workout, so
 * only the apps that log workouts write these — web reads them back like anything else a set
 * carries. `startedAt` and `endedAt` are the stretch it played for rather than the part of it
 * that fell inside the set, and `endedAt` is null on one still playing when the set was
 * written down.
 */
export interface Song {
  readonly title: string
  readonly artist?: string | null
  readonly album?: string | null
  /** The package that played it, so it can be named. */
  readonly app?: string | null
  readonly startedAt?: number | null
  readonly endedAt?: number | null
}

export interface LoggedSet {
  readonly id: string
  readonly exerciseName: string
  /** Its logging method: which numbers it holds. A weight of zero is a weight recorded. */
  readonly records: Measurements
  readonly workoutId: string | null
  readonly performedAt: number
  readonly weightKg: number | null
  readonly reps: number | null
  readonly durationSeconds: number | null
  readonly distanceMeters: number | null
  readonly isWarmup: boolean
  readonly notes: string | null
  readonly rpe: number | null
  /**
   * Which superset this set was performed in, and which round of it. Unique within one
   * workout, which is all the grouping asks. Null on a set that was part of none.
   */
  readonly supersetGroup?: string | null
  readonly supersetRound?: number | null
  /**
   * When the set was performed, read off its recording: the wall clock at the start of the
   * take, plus where the kept part of it begins and ends. Null on a set nothing was filmed of,
   * and on every set logged before sets carried these — `performedAt` is when it was written
   * down, which is the only answer those have.
   */
  readonly startedAt?: number | null
  readonly endedAt?: number | null
  /**
   * What was playing while it was performed, in the order it played. Empty on a set nothing
   * was playing over, and on every set logged before sets carried these.
   */
  readonly songs?: readonly Song[]
  readonly hasVideo: boolean
  readonly hasThumbnail: boolean
  /** How many bytes the video weighs — what says which video a face map describes. */
  readonly videoSize?: number | null
  /** Whether the phone that found its faces has sent their map up — see `/specs/features/faces.spec.md`. */
  readonly hasFaceMap?: boolean
}

/** A run of a day's sets that belong together — one workout, or none at all — as one block. */
export interface TimelineGroup {
  readonly workoutId: string | null
  readonly workoutName: string | null
  readonly count: number
  /**
   * How many of them are warm-ups, which is what lets the timeline work out how many tiles the
   * block keeps before any of its sets have loaded.
   */
  readonly warmups: number
}

/**
 * How many sets fall on one day, and how they split between the workouts they were part of.
 * The whole history's worth of these is cheap to fetch and is what lets the timeline know
 * its own height before it has loaded any sets.
 */
export interface TimelineDay {
  readonly day: string
  readonly count: number
  readonly groups: TimelineGroup[]
}

export interface Workout {
  readonly id: string
  readonly name: string | null
  readonly startedAt: number
  readonly finishedAt: number | null
  readonly mood?: Mood | null
  readonly openingNotes?: string | null
  readonly closingNotes?: string | null
  readonly setCount?: number
  /** The run it was played under, when it came from a template. */
  readonly enrollmentId?: string | null
  readonly templateWorkout?: string | null
  /**
   * Every set the template planned, logged or not. Held on the device until the workout
   * reaches the server, which is where it lives afterwards.
   */
  readonly plan?: PlanEntry[]
}

/**
 * The settings that follow the account rather than the device: what a workout shows, what a
 * day's weigh-in offers to log and asks for, and what a video's two gestures are worth. Every
 * one has a value at all times — the server decides the defaults, so no client has an opinion
 * about what an account that has never chosen means.
 *
 * What stays on the device is what belongs to it: the browser's own reminder, the timeline's
 * filters, whether a video plays out loud in this room.
 */
export interface AccountSettings {
  /** Whether a workout shows its warm-up sets among the working ones. Android only, so far. */
  readonly showWarmups: boolean
  readonly workoutsInDiary: boolean
  /** What a swipe across an entry on the food log does, one answer per direction. */
  readonly diarySwipeRight: string
  readonly diarySwipeLeft: string
  /** Whether rest happens at all. Android only, so far. */
  /** Whether the app carries the nudge while today's weigh-in has no weight on it. */
  readonly weighInNudge: boolean
  /** The minute of the day the nudge starts standing from, past midnight. */
  readonly weighInNudgeFrom: number
  /** The measurements taken routinely, offered in front of the weigh-in's fold. */
  readonly weighInRoutine: readonly Measurement[]
  /** What a held side of a video plays at, in hundredths: 50 is half speed, 200 is double. */
  readonly holdSlowPercent: number
  readonly holdFastPercent: number
  /** How far a double tap on each side jumps, in seconds. */
  readonly jumpBackSeconds: number
  readonly jumpForwardSeconds: number
  /**
   * Which faces a video that leaves the app starts with blurred, and whether the faces step
   * asks first — see `/specs/features/faces.spec.md`.
   */
  readonly shareFaces: 'others' | 'all' | 'none'
  readonly shareFacesAsk: boolean
  /** The body stats the expenditure is estimated from — see the nutrition spec. */
  readonly birthDate: string | null
  readonly sex: 'female' | 'male' | null
  /** Kept in metres like every stored length; entered in centimetres. */
  readonly heightM: number | null
  readonly activityLevel: 'sedentary' | 'light' | 'moderate' | 'active'
}

/**
 * What the account's plan allows and how much of it has gone. `storageBytes` of zero is the
 * whole of what "no cloud" means: nothing of the account is kept on the server, so there is
 * nothing for this app to read.
 */
export interface Plan {
  readonly id: string
  readonly name: string
  readonly storageBytes: number
  readonly aiCallsPerMonth: number
  readonly storageUsedBytes: number
  readonly aiCallsUsed: number
  /** The month's AI allowance and what is spent of it, in tokens — what the AI is held to. */
  readonly aiTokensPerMonth: number
  readonly aiTokensUsed: number
}

export interface User {
  readonly id: string
  readonly email: string
  readonly name: string
  /** The picture on the Google account, or null for an account carrying none. */
  readonly picture: string | null
  readonly units: Units | null
  readonly distanceUnits: DistanceUnits | null
  /** Whether this account reads feedback. The server decides; no app offers a way in. */
  readonly isAdmin: boolean
  readonly settings: AccountSettings
  /**
   * Absent only on a session stored by a build that predates plans. Nothing is gated on a
   * plan that is not there: the account is read again on every load, and locking somebody
   * out of the app on the strength of a field this device simply has not heard of yet would
   * be the worst possible reading of it.
   */
  readonly plan?: Plan
}

export type FeedbackStatus = 'new' | 'acknowledged' | 'resolved' | 'rejected'

export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = [
  'new',
  'acknowledged',
  'resolved',
  'rejected',
]

/** A piece of feedback, as an admin reads it back: the text, who sent it, and where it got to. */
export interface Feedback {
  readonly id: string
  readonly body: string
  readonly status: FeedbackStatus
  readonly createdAt: number
  readonly fromName: string
  readonly fromEmail: string
}

export interface Session {
  readonly token: string
  readonly user: User
}

/** The numbers a set records; which ones are filled is what its logging method is read from. */
export interface SetNumbersInput {
  readonly weightKg?: number
  readonly reps?: number
  readonly durationSeconds?: number
  readonly distanceMeters?: number
  readonly isWarmup?: boolean
  readonly notes?: string | null
  readonly rpe?: number | null
  /** Which superset it was performed in, and which round of it. See `LoggedSet`. */
  readonly supersetGroup?: string | null
  readonly supersetRound?: number | null
  /** When it was performed, off its recording. See `LoggedSet`. */
  readonly startedAt?: number | null
  readonly endedAt?: number | null
}

export interface SetInput extends SetNumbersInput {
  readonly exerciseName: string
  readonly performedAt: number
  /** Existing set reused while replacing a completed workout; never sent to the server. */
  readonly sourceSetId?: string
}

/**
 * A write waiting to reach the server. Ops carry ids, not payloads — the flush reads the
 * current row, so an edit made while offline is sent in its final shape, once.
 */
/**
 * What a queued write carries beyond what it is: whether the server refused it, and what it
 * said. A refusal is kept rather than dropped — nothing retries on its own, so the record is
 * the only thing that can offer trying again, and the only thing that survives a reload.
 */
interface Refusable {
  readonly failed?: boolean
  readonly error?: string
}

export type PendingOp = Refusable &
  (
    | { readonly id: string; readonly kind: 'put-set'; readonly setId: string }
    | { readonly id: string; readonly kind: 'delete-set'; readonly setId: string }
    | { readonly id: string; readonly kind: 'put-workout'; readonly workoutId: string }
    | { readonly id: string; readonly kind: 'delete-workout'; readonly workoutId: string }
    | { readonly id: string; readonly kind: 'put-template'; readonly templateId: string }
    | { readonly id: string; readonly kind: 'delete-template'; readonly templateId: string }
    | { readonly id: string; readonly kind: 'enroll'; readonly enrollmentId: string }
    /** Carries no weekday of its own: what the run holds when this lands is what is sent. */
    | { readonly id: string; readonly kind: 'set-anchor'; readonly enrollmentId: string }
    | { readonly id: string; readonly kind: 'end-enrollment'; readonly enrollmentId: string }
    | { readonly id: string; readonly kind: 'delete-enrollment'; readonly enrollmentId: string }
    /** An entry logged. Carries no row: what the mirror holds when this lands is what is sent. */
    | { readonly id: string; readonly kind: 'put-entry'; readonly entryId: string }
  )

/** Clears a refusal off an op. The keys go rather than turning undefined, which the store
 * would otherwise write back as present-but-empty. */
export function withoutRefusal<T extends PendingOp>(op: T): T {
  const { failed: _failed, error: _error, ...rest } = op
  return rest as T
}

/** The four numbers a set can hold, with anything unsaid as null. */
export function numbersOf(input: SetNumbersInput) {
  return {
    weightKg: input.weightKg ?? null,
    reps: input.reps ?? null,
    durationSeconds: input.durationSeconds ?? null,
    distanceMeters: input.distanceMeters ?? null,
  }
}

/**
 * The logging method a set's numbers add up to. A weight of zero is a weight recorded, and so
 * counts here like any other — it is what separates a weighted set from an unweighted one.
 */
export function recordsOf(numbers: {
  weightKg: number | null
  reps: number | null
  durationSeconds: number | null
  distanceMeters: number | null
}): Measurements {
  return {
    weight: numbers.weightKg !== null,
    reps: numbers.reps !== null,
    duration: numbers.durationSeconds !== null,
    distance: numbers.distanceMeters !== null,
  }
}
